import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import type { RouterOutputs } from "@/trpc";
import {
  exclusiveEndToLastDay,
  formatCalendarDate,
  instantToDate,
} from "../utils/wage-dates";
import { formatWageSummary } from "../utils/wage-fields";

type OrganizationWageSetListItem =
  RouterOutputs["organizationWageSet"]["list"][number];

type OrganizationWageSetsListProps = {
  organizationWageSets: OrganizationWageSetListItem[];
  organizationBusinessUnitId: string;
  timezoneId?: string;
};

/** Active orgWageSets first, then retired ones; the API already sorts by name. */
function sortActiveFirst(
  organizationWageSets: OrganizationWageSetListItem[],
): OrganizationWageSetListItem[] {
  const active = organizationWageSets.filter(
    (orgWageSet) => orgWageSet.retiredAt === null,
  );
  const retired = organizationWageSets.filter(
    (orgWageSet) => orgWageSet.retiredAt !== null,
  );
  return [...active, ...retired];
}

function formatAssignmentCount(count: number): string {
  if (count === 1) {
    return "1 open assignment";
  }
  return `${count} open assignments`;
}

function describeOrgWageSetPeriod({
  orgWageSet,
  timezoneId,
}: {
  orgWageSet: OrganizationWageSetListItem;
  timezoneId?: string;
}): string | null {
  if (orgWageSet.retiredAt) {
    const lastDay = formatCalendarDate(
      exclusiveEndToLastDay({ instant: orgWageSet.retiredAt, timezoneId }),
    );
    return `Retired, last day ${lastDay}`;
  }
  if (!orgWageSet.latestVersion) {
    return null;
  }
  const since = formatCalendarDate(
    instantToDate({
      instant: orgWageSet.latestVersion.version.effectiveStartsAt,
      timezoneId,
    }),
  );
  return `Since ${since}`;
}

export function OrganizationWageSetsList({
  organizationWageSets,
  organizationBusinessUnitId,
  timezoneId,
}: OrganizationWageSetsListProps) {
  return (
    <ul className="space-y-3">
      {sortActiveFirst(organizationWageSets).map((orgWageSet) => {
        const isRetired = orgWageSet.retiredAt !== null;
        const period = describeOrgWageSetPeriod({ orgWageSet, timezoneId });
        const summary = orgWageSet.latestVersion
          ? formatWageSummary(orgWageSet.latestVersion.wages)
          : "";
        return (
          <li key={orgWageSet.id}>
            <Link
              to="/org/bu/$organizationBusinessUnitId/jobs/wage-sets/$organizationWageSetId"
              params={{
                organizationBusinessUnitId,
                organizationWageSetId: orgWageSet.id,
              }}
              className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              <Card
                className={`rounded-2xl border border-slate-200/80 shadow-sm transition-colors hover:border-slate-300 ${
                  isRetired ? "bg-slate-50 opacity-70" : "bg-white"
                }`}
              >
                <CardContent className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate font-semibold text-slate-900">
                        {orgWageSet.name}
                      </h2>
                      {isRetired && <Badge variant="secondary">Retired</Badge>}
                    </div>
                    {summary && (
                      <p className="text-sm text-slate-700">{summary}</p>
                    )}
                    <p className="text-sm text-slate-500">
                      {[
                        period,
                        formatAssignmentCount(orgWageSet.openAssignmentCount),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <ChevronRight
                    aria-hidden="true"
                    className="h-5 w-5 shrink-0 text-slate-400"
                  />
                </CardContent>
              </Card>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
